import React, { Fragment, useState, useEffect } from "react";
import Topbar from "../layouts/TopBar";
import RotaResources from "../resources/RotaResources";
import LocationResources from "../resources/LocationResources";
import ConfigResources from "../resources/ConfigResources";
import toast from 'react-hot-toast';
import { logout } from "../redux/actions/LoggedInAction";
import { connect } from "react-redux";
import Select from 'react-select';
import moment from "moment";
import {ACCESS_DENIED} from '../Messages'

const ROLE_ORDER = ['producer', 'directing', 'propresenter', 'live streaming', 'stage manager', 'camera 1', 'roam 1', 'roam 2', 'run around', 'announcement']

const sortedRoleKeys = (roles) => Object.keys(roles).sort((a, b) => {
    const ai = ROLE_ORDER.indexOf(a.toLowerCase())
    const bi = ROLE_ORDER.indexOf(b.toLowerCase())
    if (ai !== -1 && bi !== -1) return ai - bi
    if (ai !== -1) return -1
    if (bi !== -1) return 1
    return a.localeCompare(b)
})

// Prefers the next upcoming Sunday of the loaded month, falling back to its first
// Sunday when the whole month is already past. Keyed off the returned payload, since
// the API may answer with a different month than the one requested.
const pickDefaultSundayKey = (body) => {
    const days = (body && body.sundays) || []
    if (days.length === 0) return 'ALL'
    const today = moment().startOf('day')
    const upcoming = days.find(s => moment(s.date, 'YYYY-MM-DD').isSameOrAfter(today))
    return (upcoming || days[0]).date
}

const rotaStyles = {
    stripWrap: {display: 'flex', gap: '10px', overflowX: 'auto', padding: '4px 2px 10px', WebkitOverflowScrolling: 'touch'},
    chipBase: {flex: '0 0 auto', position: 'relative', cursor: 'pointer', font: 'inherit', lineHeight: 1.2, border: '2px solid #d7dde5', backgroundColor: '#fff', borderRadius: '10px', padding: '8px 16px', minWidth: '86px', textAlign: 'center', color: '#003165'},
    chipSelected: {backgroundColor: '#003165', borderColor: '#003165', color: '#ffa800', boxShadow: '0 2px 6px rgba(0,49,101,0.25)'},
    chipDay: {display: 'block', fontSize: '18px', fontWeight: 'bold'},
    chipMonth: {fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.85},
    chipDot: {position: 'absolute', top: '6px', right: '6px', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ffa800'},
    chipDotSelected: {boxShadow: '0 0 0 2px #003165'},
    allChip: {flex: '0 0 auto', cursor: 'pointer', font: 'inherit', lineHeight: 1.2, border: '2px solid #d7dde5', backgroundColor: '#fff', borderRadius: '10px', padding: '8px 16px', minWidth: '110px', textAlign: 'center', color: '#003165', fontSize: '13px', fontWeight: 'bold'},
    panel: {marginBottom: '34px'},
    panelHeader: {display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginBottom: '16px'},
    panelTitle: {color: '#ffa800', margin: 0, fontSize: '22px'},
    panelService: {color: '#003165', fontWeight: 'bold', fontSize: '14px'},
    teamPill: {backgroundColor: '#003165', color: '#ffa800', fontWeight: 'bold', padding: '4px 14px', borderRadius: '20px', fontSize: '14px'},
    gridContainer: {display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', alignItems: 'start'},
    roleCard: {border: '1px solid #dfe3e8', borderRadius: '10px', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden'},
    roleCardMine: {border: '2px solid #ffa800', boxShadow: '0 0 0 3px rgba(255,168,0,0.15)'},
    cardHeader: {backgroundColor: '#003165', color: '#ffa800', padding: '10px 14px', fontWeight: 'bold', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.6px'},
    serviceBar: {backgroundColor: '#eef2f7', color: '#003165', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.6px', padding: '5px 14px', borderBottom: '1px solid #dfe3e8'},
    servicePeople: {padding: '10px 14px'},
    peopleWrap: {display: 'flex', flexWrap: 'wrap', gap: '8px'},
    personChip: {display: 'inline-flex', alignItems: 'center', gap: '8px', minWidth: 0, maxWidth: '100%', backgroundColor: '#f6f8fa', border: '1px solid #e3e7ec', borderRadius: '999px', padding: '4px 12px 4px 4px'},
    personChipMine: {backgroundColor: '#fff4e0', border: '2px solid #ffa800'},
    chipAvatar: {width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ffa800', flexShrink: 0, backgroundColor: '#e9edf2'},
    chipAvatarFallback: {width: '30px', height: '30px', borderRadius: '50%', border: '2px solid #ffa800', flexShrink: 0, backgroundColor: '#e9edf2', color: '#003165', fontSize: '11px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', justifyContent: 'center'},
    chipName: {color: '#003165', fontWeight: 'bold', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'},
    youBadge: {backgroundColor: '#003165', color: '#ffa800', fontSize: '9px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '999px', letterSpacing: '0.5px', flexShrink: 0},
    unassigned: {display: 'inline-flex', alignItems: 'center', color: '#9aa4b1', fontSize: '12px', fontStyle: 'italic', border: '1px dashed #d9dee5', borderRadius: '999px', padding: '5px 12px', backgroundColor: '#fafbfc'}
}

const ChipAvatar = ({ user }) => {
    if (user.image) {
        return <img src={user.image} alt={`${user.firstname} ${user.lastname}`} style={rotaStyles.chipAvatar} />
    }
    const initials = `${(user.firstname || '').charAt(0)}${(user.lastname || '').charAt(0)}`.toUpperCase()
    return <span style={rotaStyles.chipAvatarFallback}>{initials}</span>
}

const PersonChip = ({ user, mine }) => {
    const fullName = `${user.firstname} ${user.lastname}`
    return (
        <span style={mine ? {...rotaStyles.personChip, ...rotaStyles.personChipMine} : rotaStyles.personChip}>
            <ChipAvatar user={user} />
            <span style={rotaStyles.chipName} title={fullName}>{fullName}</span>
            {mine && <span style={rotaStyles.youBadge}>YOU</span>}
        </span>
    )
}

const RoleCard = ({ roleName, services, isMe, showServiceLabels }) => {
    const mineHere = services.some(service => ((service.roles || {})[roleName] || []).some(isMe))
    return (
        <div style={mineHere ? {...rotaStyles.roleCard, ...rotaStyles.roleCardMine} : rotaStyles.roleCard}>
            <div style={rotaStyles.cardHeader}>{roleName}</div>
            {services.map((service, serviceIndex) => {
                const people = (service.roles || {})[roleName] || []
                return (
                    <div key={serviceIndex}>
                        {showServiceLabels && (
                            <div style={serviceIndex === 0
                                ? rotaStyles.serviceBar
                                : {...rotaStyles.serviceBar, borderTop: '1px solid #dfe3e8'}}>
                                {service.service_name}
                            </div>
                        )}
                        <div style={{...rotaStyles.servicePeople, ...rotaStyles.peopleWrap}}>
                            {people.length > 0
                                ? people.map((user, userIndex) => (
                                    <PersonChip key={user.id || userIndex} user={user} mine={isMe(user)} />
                                ))
                                : <span style={rotaStyles.unassigned}>Unassigned</span>}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

const RoleCardGrid = ({ services, isMe, showServiceLabels }) => {
    const roleNames = sortedRoleKeys(services.reduce((acc, service) => {
        Object.keys(service.roles || {}).forEach(name => { acc[name] = true })
        return acc
    }, {}))

    return (
        <div style={rotaStyles.gridContainer}>
            {roleNames.map(roleName => (
                <RoleCard
                    key={roleName}
                    roleName={roleName}
                    services={services}
                    isMe={isMe}
                    showServiceLabels={showServiceLabels}
                />
            ))}
        </div>
    )
}

const SundayPanel = ({ sunday, isCanaryWharf, isMe }) => {
    const services = sunday.services || []
    const showServiceLabels = services.length > 1
    const team = services[0] ? services[0].canary_wharf_team : null

    return (
        <div style={rotaStyles.panel}>
            <div style={rotaStyles.panelHeader}>
                <h3 style={rotaStyles.panelTitle}>
                    <strong>{moment(sunday.date, "YYYY-MM-DD").format('DD MMMM YYYY')} · {sunday.day_of_week}</strong>
                </h3>
                {!showServiceLabels && services[0] && (
                    <span style={rotaStyles.panelService}>{services[0].service_name}</span>
                )}
                {isCanaryWharf && team && <span style={rotaStyles.teamPill}>{team} Covering</span>}
            </div>
            <RoleCardGrid services={services} isMe={isMe} showServiceLabels={showServiceLabels} />
        </div>
    )
}

const SundayStrip = ({ sundays, selectedKey, onSelect, myDates }) => (
    <div style={rotaStyles.stripWrap}>
        {sundays.map(sunday => {
            const selected = sunday.date === selectedKey
            const date = moment(sunday.date, "YYYY-MM-DD")
            return (
                <button
                    type="button"
                    key={sunday.date}
                    onClick={() => onSelect(sunday.date)}
                    style={selected ? {...rotaStyles.chipBase, ...rotaStyles.chipSelected} : rotaStyles.chipBase}
                >
                    <span style={rotaStyles.chipDay}>{date.format('DD')}</span>
                    <span style={rotaStyles.chipMonth}>{date.format('MMM')}</span>
                    {myDates.has(sunday.date) && (
                        <span style={selected ? {...rotaStyles.chipDot, ...rotaStyles.chipDotSelected} : rotaStyles.chipDot} />
                    )}
                </button>
            )
        })}
        <button
            type="button"
            onClick={() => onSelect('ALL')}
            style={selectedKey === 'ALL' ? {...rotaStyles.allChip, ...rotaStyles.chipSelected} : rotaStyles.allChip}
        >
            All Sundays
        </button>
    </div>
)

function Rota({isLoggedIn, loggedInUser, logout}){

    const [viewType, setViewType] = useState('monthly')
    const [rota, setRota] = useState(null)
    const [selectedLocation, setSelectedLocation] = useState(null)
    const [selectedEvent, setSelectedEvent] = useState(null)
    const [locations, setLocations] = useState([])
    const [eventRotas, setEventRotas] = useState([])
    const [monthYear, setMonthYear] = useState({ month: '', year: '', monthName: '' })
    const [cwTeams, setCwTeams] = useState({})
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
    const [nextMonthStatus, setNextMonthStatus] = useState({ available: false, monthName: '', year: '', month: null })
    const [loadingNextMonth, setLoadingNextMonth] = useState(false)
    const [selectedSundayKey, setSelectedSundayKey] = useState('ALL')

    let rota_resources = new RotaResources()
    const locationResources = new LocationResources()
    const configResources = new ConfigResources()

    const loadMonthlyRota = (locationId) => {
        const currentDate = moment().format('DD-MM-YYYY HH:mm:ss')
        return rota_resources.get_monthly_rota_user(currentDate, locationId)
    }

    const loadEventRotas = () => {
        return rota_resources.get_event_rotas_user()
    }

    const loadEventRotaDetails = (eventId) => {
        return rota_resources.get_event_rota_user(eventId)
    }

    const loadLocations = () => {
        return locationResources.get()
    }

    const handleViewTypeChange = (e) => {
        setViewType(e.target.value)
        setRota(null)
        setSelectedLocation(null)
        setSelectedEvent(null)
        setMonthYear({ month: '', year: '', monthName: '' })
        setNextMonthStatus({ available: false, monthName: '', year: '', month: null })
        setSelectedSundayKey('ALL')
    }

    const handleLocationSelection = (selectedOption) => {
        setSelectedLocation(selectedOption)
        setNextMonthStatus({ available: false, monthName: '', year: '', month: null })
        loadMonthlyRota(selectedOption.value).then(response => {
            if (response.error && response.error.length > 0) {
                toast.error("Failed to load rota: " + response.error)
            } else {
                const body = response['body']
                setRota(body)
                const loadedMonth = body['month']
                const loadedYear = body['year']
                setMonthYear({ month: loadedMonth, year: loadedYear, monthName: body['month_name'] })
                rota_resources.check_next_month_user(selectedOption.value, loadedMonth, loadedYear).then(checkResp => {
                    if (checkResp.body) {
                        setNextMonthStatus({
                            available: checkResp.body.available,
                            monthName: checkResp.body.month_name,
                            year: checkResp.body.year,
                            month: checkResp.body.month
                        })
                    }
                }).catch(() => {})
            }
        }).catch((err) => {
            toast.error("Network error: " + err)
        })
    }

    const handleLoadNextMonthRota = () => {
        if (!selectedLocation) return
        setLoadingNextMonth(true)
        rota_resources.get_next_month_rota_user(selectedLocation.value, nextMonthStatus.month, nextMonthStatus.year).then(response => {
            setLoadingNextMonth(false)
            if (response.error && response.error.length > 0) {
                toast.error("Failed to load next month's rota: " + response.error)
            } else {
                const body = response['body']
                setRota(body)
                setMonthYear({ month: body['month'], year: body['year'], monthName: body['month_name'] })
                setNextMonthStatus({ available: false, monthName: '', year: '', month: null })
                rota_resources.check_next_month_user(selectedLocation.value, body['month'], body['year']).then(resp => {
                    if (resp.body) {
                        setNextMonthStatus({
                            available: resp.body.available,
                            monthName: resp.body.month_name,
                            year: resp.body.year,
                            month: resp.body.month
                        })
                    }
                }).catch(() => {})
            }
        }).catch((err) => {
            setLoadingNextMonth(false)
            toast.error("Network error: " + err)
        })
    }

    const handleEventSelection = (selectedOption) => {
        setSelectedEvent(selectedOption)
        if (selectedOption) {
            loadEventRotaDetails(selectedOption.value).then(response => {
                const eventData = response['body']

                // Transform event rota data to display format
                const rotaData = {
                    event_id: eventData.event_id,
                    event_name: eventData.event_name,
                    event_date: eventData.event_date,
                    formatted_date: eventData.formatted_date,
                    rota_id: eventData.rota_id,
                    enabled: eventData.enabled,
                    location_id: selectedOption.location_id,
                    sundays: [{
                        date: moment(eventData.event_date).format('YYYY-MM-DD'),
                        day_of_week: moment(eventData.event_date).format('dddd'),
                        services: [{
                            service_name: selectedOption.service_name,
                            canary_wharf_team: selectedOption.team,
                            roles: eventData.roles || {}
                        }]
                    }]
                }

                setRota(rotaData)
            }).catch((err) => {
                toast.error("Network error: " + err)
            })
        }
    }

    useEffect(() => {
        loadLocations().then(response => {
            setLocations(response['body'])
            if (response['body'].length > 0) {
                const firstLocation = {
                    value: response['body'][0]['id'],
                    label: response['body'][0]['name']
                }
                setSelectedLocation(firstLocation)
                handleLocationSelection(firstLocation)
            }
        }).catch((err) => {
            toast.error("Network error: " + err)
        })

        loadEventRotas().then(response => {
            setEventRotas(response['body']['event_rotas'] || [])
        }).catch((err) => {
            toast.error("Network error: " + err)
        })

        configResources.get_cw_teams().then(response => {
            setCwTeams(response['body'] || {})
        }).catch(() => {})

        const handleResize = () => setIsMobile(window.innerWidth <= 768)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    // setRota always receives a freshly-parsed object, so this covers location changes,
    // "load next month" and event selection without each having to reset the strip itself.
    useEffect(() => {
        if (rota) setSelectedSundayKey(pickDefaultSundayKey(rota))
    }, [rota])

    // Filter locations that use a rota
    let mediaLocations = []
    locations
        .filter(loc => loc['uses_rota'] === "True" || loc['uses_rota'] === true || loc['uses_rota'] === 1)
        .map((loc, index) => (
            mediaLocations.push({value: loc['id'], label: loc['name'] })
        ))

    const isCanaryWharf = selectedLocation && selectedLocation.label.toLowerCase().includes('canary wharf')
    const isLibertyPoint = selectedLocation && selectedLocation.label.toLowerCase().includes('liberty point')

    // Backend as_dict() stringifies ids, so compare loosely and fall back to email.
    const isMe = (user) => {
        if (!user || !loggedInUser) return false
        if (user.id != null && loggedInUser.id != null && String(user.id) === String(loggedInUser.id)) return true
        return !!(user.email && loggedInUser.email &&
            String(user.email).toLowerCase() === String(loggedInUser.email).toLowerCase())
    }

    const allSundays = (rota && rota.sundays) || []
    const myDates = new Set(
        allSundays
            .filter(sunday => (sunday.services || []).some(service =>
                Object.values(service.roles || {}).some(people => (people || []).some(isMe))))
            .map(sunday => sunday.date)
    )
    const visibleSundays = selectedSundayKey === 'ALL'
        ? allSundays
        : [allSundays.find(sunday => sunday.date === selectedSundayKey) || allSundays[0]].filter(Boolean)

    const CWScheduleTable = () => (
        <table style={{borderCollapse: 'collapse', fontSize: 'clamp(11px, 2.8vw, 13px)', width: '100%', maxWidth: '480px'}}>
            <thead>
                <tr style={{backgroundColor: '#003165', color: '#fff'}}>
                    <th style={{padding: '6px 10px', border: '1px solid #aaa', textAlign: 'left'}}></th>
                    <th style={{padding: '6px 10px', border: '1px solid #aaa', textAlign: 'center'}}>Team Meeting</th>
                    <th style={{padding: '6px 10px', border: '1px solid #aaa', textAlign: 'center'}}>1st Service</th>
                </tr>
            </thead>
            <tbody>
                {[
                    ['Call Time',     '7:45 - 8:00 AM',  '8:30 - 8:45 AM'],
                    ['Setup',         '8:00 - 8:30 AM',  '8:45 - 9:45 AM'],
                    ['Prayer',        '8:35 - 8:45 AM',  '9:45 - 9:50 AM'],
                    ['Service Starts','8:45 AM',          '9:52 AM'],
                ].map(([label, tm, s1], i) => (
                    <tr key={i} style={{backgroundColor: i % 2 === 0 ? '#fff' : '#f8f9fa'}}>
                        <td style={{padding: '5px 10px', border: '1px solid #e0e0e0', fontWeight: 'bold', color: '#003165'}}>{label}</td>
                        <td style={{padding: '5px 10px', border: '1px solid #e0e0e0', textAlign: 'center'}}>{tm}</td>
                        <td style={{padding: '5px 10px', border: '1px solid #e0e0e0', textAlign: 'center'}}>{s1}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    const LPScheduleTable = () => (
        <table style={{borderCollapse: 'collapse', fontSize: 'clamp(11px, 2.8vw, 13px)', width: '100%', maxWidth: '480px'}}>
            <thead>
                <tr style={{backgroundColor: '#003165', color: '#fff'}}>
                    <th style={{padding: '6px 10px', border: '1px solid #aaa', textAlign: 'left'}}></th>
                    <th style={{padding: '6px 10px', border: '1px solid #aaa', textAlign: 'center'}}>Call Time</th>
                    <th style={{padding: '6px 10px', border: '1px solid #aaa', textAlign: 'center'}}>Service Starts</th>
                </tr>
            </thead>
            <tbody>
                {[
                    ['Team Members Meeting', '07:50', '08:45'],
                    ['1st Service',          '09:00', '10:00'],
                    ['2nd Service',          '11:00', '12:00'],
                    ['3rd Service',          '—',     '02:30'],
                ].map(([label, call, start], i) => (
                    <tr key={i} style={{backgroundColor: i % 2 === 0 ? '#fff' : '#f8f9fa'}}>
                        <td style={{padding: '5px 10px', border: '1px solid #e0e0e0', fontWeight: 'bold', color: '#003165'}}>{label}</td>
                        <td style={{padding: '5px 10px', border: '1px solid #e0e0e0', textAlign: 'center'}}>{call}</td>
                        <td style={{padding: '5px 10px', border: '1px solid #e0e0e0', textAlign: 'center'}}>{start}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    const currentMonth = moment()
    const nextMonth = moment().add(1, 'month')
    const crossesYear = currentMonth.year() !== nextMonth.year()

    // Derived from the month actually loaded rather than the clock: the API can answer
    // with a different month than the one requested.
    const loadedMonthStart = monthYear.month && monthYear.year
        ? moment({ year: Number(monthYear.year), month: Number(monthYear.month) - 1, day: 1 })
        : null
    const lastSundayOfLoadedMonth = loadedMonthStart && (() => {
        const d = loadedMonthStart.clone().endOf('month').startOf('day')
        while (d.day() !== 0) d.subtract(1, 'day')
        return d
    })()
    const isAfterLastSunday = !!lastSundayOfLoadedMonth && moment().startOf('day').isAfter(lastSundayOfLoadedMonth)

    const viewingNextMonthRota = rota && nextMonthStatus.month &&
        monthYear.month === nextMonthStatus.month && monthYear.year === nextMonthStatus.year

    return(
        <Fragment>
            <Topbar/>

            <div className="hero-area">
            <div className="page-banner parallax" style={{backgroundColor:"#003165"}}>
                    <div className="container">                    
                        <div className="page-banner-text">
                            {isLoggedIn ?
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>Rota</strong></h1>
                                :
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>{ACCESS_DENIED}</strong></h1>
                            }                            
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
            {
                isLoggedIn ?
                <>
        	<div className="container">

                <div className="spacer-40"></div>

                <div>
                    <div className="row">
                        <div className="col-md-12">
                            <h3 style={{color:"#ffa800"}}><strong>Rota Type</strong></h3>
                            <div className="spacer-20"></div>
                            <div>
                                <label style={{marginRight: "20px"}}>
                                    <input
                                        type="radio"
                                        value="monthly"
                                        checked={viewType === 'monthly'}
                                        onChange={handleViewTypeChange}
                                        style={{marginRight: "5px"}}
                                    />
                                    Monthly Rota
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        value="event"
                                        checked={viewType === 'event'}
                                        onChange={handleViewTypeChange}
                                        style={{marginRight: "5px"}}
                                    />
                                    Event Rota
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="spacer-40"></div>

                    {viewType === 'monthly' ? (
                        <>
                        <div className="row">
                            <div className="col-md-6">
                                <label style={{color:"#ffa800"}}><strong>Location</strong></label>
                                <Select
                                    name='location'
                                    id="location"
                                    classNamePrefix='select-css-tag'
                                    value={selectedLocation}
                                    onChange={handleLocationSelection}
                                    options={mediaLocations}
                                    placeholder="Select Location"
                                />
                            </div>

                            <div className="col-md-6">
                                {monthYear.monthName && (
                                    <div>
                                        <h2 style={{color:"#003165"}}>
                                            <strong>Rota for {monthYear.monthName} {monthYear.year}</strong>
                                        </h2>
                                    </div>
                                )}
                            </div>
                        </div>

                        {selectedLocation && (
                            <div style={{marginTop: '20px'}}>
                                {isAfterLastSunday && (
                                    <div style={{
                                        backgroundColor: '#fff8e6',
                                        border: '2px solid #ffa800',
                                        borderRadius: '8px',
                                        padding: '12px 18px',
                                        color: '#7a5200',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}>
                                        The last Sunday of {monthYear.monthName} {monthYear.year} has passed — next month's rota is now the default view.
                                    </div>
                                )}
                                {nextMonthStatus.available && !viewingNextMonthRota && (
                                    <div style={{marginTop: isAfterLastSunday ? '10px' : '0'}}>
                                        <button
                                            onClick={handleLoadNextMonthRota}
                                            disabled={loadingNextMonth}
                                            style={{
                                                backgroundColor: '#003165',
                                                color: '#ffa800',
                                                border: '2px solid #003165',
                                                borderRadius: '6px',
                                                padding: '8px 20px',
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                cursor: loadingNextMonth ? 'not-allowed' : 'pointer',
                                                opacity: loadingNextMonth ? 0.7 : 1
                                            }}
                                        >
                                            {loadingNextMonth
                                                ? 'Loading...'
                                                : `Load ${nextMonthStatus.monthName} ${nextMonthStatus.year} Rota`}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        </>
                    ) : (
                        <div className="row">
                            <div className="col-md-6">
                                <label style={{color:"#ffa800"}}><strong>Select Event ({currentMonth.format(crossesYear ? 'MMMM YYYY' : 'MMMM')} & {nextMonth.format('MMMM YYYY')})</strong></label>
                                <Select
                                    name='event'
                                    id="event"
                                    classNamePrefix='select-css-tag'
                                    value={selectedEvent}
                                    onChange={handleEventSelection}
                                    options={eventRotas
                                        .filter(eventRota => {
                                            const eventDate = moment(eventRota.event_date)
                                            const isCurrentMonth = eventDate.month() === currentMonth.month() && eventDate.year() === currentMonth.year()
                                            const isNextMonth = eventDate.month() === nextMonth.month() && eventDate.year() === nextMonth.year()
                                            const isEnabled = eventRota.enabled === '1' || eventRota.enabled === 1 || eventRota.enabled === true
                                            return (isCurrentMonth || isNextMonth) && isEnabled
                                        })
                                        .map(eventRota => ({
                                            value: eventRota.event_id,
                                            label: `${eventRota.event_name} - ${moment(eventRota.event_date).format('DD MMM YYYY')}`,
                                            location_id: eventRota.location_id,
                                            service_name: eventRota.service_name,
                                            team: eventRota.team
                                        }))}
                                    placeholder="Select Event"
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="spacer-40"></div>
            </div>

                {/* Liberty Point schedule info */}
                {isLibertyPoint && rota && (
                    <div className="container" style={{marginBottom: '30px'}}>
                        <h4 style={{color: '#003165', marginBottom: '12px'}}><strong>Service Schedule</strong></h4>
                        <LPScheduleTable />
                    </div>
                )}

                {/* Canary Wharf team legend + schedule table */}
                {isCanaryWharf && (
                    <div className="container" style={{marginBottom: '30px'}}>
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: '30px', alignItems: 'flex-start'}}>
                            {Object.keys(cwTeams).length > 0 && (
                                <div>
                                    <h4 style={{color: '#003165', marginBottom: '12px'}}><strong>Team Legend</strong></h4>
                                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '16px'}}>
                                        {Object.entries(cwTeams).map(([teamName, members]) => (
                                            <div key={teamName} style={{
                                                backgroundColor: '#f8f9fa',
                                                border: '2px solid #ffa800',
                                                borderRadius: '8px',
                                                padding: '14px 18px',
                                                minWidth: '180px'
                                            }}>
                                                <div style={{color: '#003165', fontWeight: 'bold', marginBottom: '8px', fontSize: '15px'}}>{teamName}</div>
                                                {members.map(member => (
                                                    <div key={member.id} style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px'}}>
                                                        {member.image && (
                                                            <img src={member.image} style={{width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ffa800'}} alt="" />
                                                        )}
                                                        <span style={{color: '#333', fontSize: '14px'}}>{member.firstname} {member.lastname}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div>
                                <h4 style={{color: '#003165', marginBottom: '12px'}}><strong>Service Schedule</strong></h4>
                                <CWScheduleTable />
                            </div>
                        </div>
                    </div>
                )}

                <div>
                    {isMobile ? (
                    /* Mobile: selected Sunday (or all) stacked vertically */
                    <>
                    {allSundays.length > 1 && (
                        <div className="container">
                            <div className="spacer-30"></div>
                            <SundayStrip
                                sundays={allSundays}
                                selectedKey={selectedSundayKey}
                                onSelect={setSelectedSundayKey}
                                myDates={myDates}
                            />
                        </div>
                    )}
                    {visibleSundays.map((sunday, sundayIndex) => {
                        const sundayTeam = sunday.services && sunday.services[0] ? sunday.services[0].canary_wharf_team : null
                        return (
                        <div key={sundayIndex}>
                            <div className="spacer-50"></div>
                            <div className="row">
                                <div className="col-md-12">
                                    <div>
                                        <h2 style={{color:"#ffa800", margin: 0}}>
                                            <strong>{moment(sunday.date, "YYYY-MM-DD").format('DD MMMM YYYY')} - {sunday.day_of_week}</strong>
                                        </h2>
                                        {isCanaryWharf && sundayTeam && (
                                            <div style={{marginTop: '6px'}}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    backgroundColor: '#003165',
                                                    color: '#ffa800',
                                                    fontWeight: 'bold',
                                                    padding: '4px 14px',
                                                    borderRadius: '20px',
                                                    fontSize: '14px'
                                                }}>{sundayTeam} Covering</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="spacer-30"></div>

                            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                                    {sunday.services.map((service, serviceIndex) => (
                                        <div key={serviceIndex} style={{
                                            border: '2px solid #003165',
                                            borderRadius: '10px',
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                                        }}>
                                            {/* Service header */}
                                            <div style={{
                                                backgroundColor: '#003165',
                                                color: '#fff',
                                                padding: '10px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between'
                                            }}>
                                                <span style={{fontWeight: 'bold', fontSize: '15px'}}>{service.service_name}</span>
                                                {isCanaryWharf && service.canary_wharf_team && (
                                                    <span style={{
                                                        backgroundColor: '#ffa800',
                                                        color: '#003165',
                                                        fontWeight: 'bold',
                                                        padding: '2px 10px',
                                                        borderRadius: '12px',
                                                        fontSize: '12px'
                                                    }}>{service.canary_wharf_team}</span>
                                                )}
                                            </div>
                                            {/* Roles list */}
                                            {service.roles && sortedRoleKeys(service.roles).map((roleName, roleIndex) => (
                                                <div key={roleName} style={{
                                                    backgroundColor: roleIndex % 2 === 0 ? '#fff' : '#f8f9fa',
                                                    borderTop: '1px solid #e0e0e0',
                                                    padding: '10px 16px'
                                                }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        marginBottom: '8px'
                                                    }}>
                                                        <span style={{
                                                            color: '#ffa800',
                                                            fontWeight: 'bold',
                                                            fontSize: '13px',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}>{roleName}</span>
                                                        <span style={{
                                                            color: '#aaa',
                                                            fontSize: '11px',
                                                            fontStyle: 'italic'
                                                        }}>{moment(sunday.date, "YYYY-MM-DD").format('DD MMM')}</span>
                                                    </div>
                                                    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                                                        {service.roles[roleName].map((user, userIndex) => (
                                                            <div key={userIndex} style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '10px'
                                                            }}>
                                                                <img
                                                                    src={user['image']}
                                                                    style={{
                                                                        width: '38px',
                                                                        height: '38px',
                                                                        borderRadius: '50%',
                                                                        objectFit: 'cover',
                                                                        border: '2px solid #ffa800',
                                                                        flexShrink: 0
                                                                    }}
                                                                    alt={`${user.firstname} ${user.lastname}`}
                                                                />
                                                                <span style={{color: '#003165', fontWeight: 'bold', fontSize: '14px'}}>
                                                                    {user.firstname} {user.lastname}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                        </div>
                        )
                    })}
                    </>
                    ) : allSundays.length > 0 && (
                        /* Desktop: one Sunday at a time (or all), as a grid of role cards */
                        <div className="container">
                            <div className="spacer-30"></div>
                            {allSundays.length > 1 && (
                                <SundayStrip
                                    sundays={allSundays}
                                    selectedKey={selectedSundayKey}
                                    onSelect={setSelectedSundayKey}
                                    myDates={myDates}
                                />
                            )}
                            <div className="spacer-20"></div>
                            {visibleSundays.map(sunday => (
                                <SundayPanel
                                    key={sunday.date}
                                    sunday={sunday}
                                    isCanaryWharf={isCanaryWharf}
                                    isMe={isMe}
                                />
                            ))}
                        </div>
                    )}
                </div>



                </>   :<></>

            }          
          

            </div>

        </Fragment>
    )
}

const mapStateToProps = (state) => {
    return{
       isLoggedIn: state.authReducer.isLoggedIn,
       loggedInUser: state.authReducer.user
    };
    
};

const mapDispatchToProps = {
    logout
};

export default connect(mapStateToProps, mapDispatchToProps)(Rota);